import React, { useState, useRef, useEffect } from "react";
import Layout from "../../Component/Layout";
import DatePicker from "react-datepicker";
import axios from "axios";
import { PURCHASE_API } from "../../apiConfig";
import "react-datepicker/dist/react-datepicker.css";
import {
    FaWallet,
    FaCalendarAlt,
    FaChartLine,
    FaSyncAlt,
    FaDownload,
    FaChevronDown,
} from "react-icons/fa";
import {
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    Tooltip,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
} from "recharts";

// ==========================================
// EMPTY / FALLBACK SHAPES (real data comes from /payables-dashboard/dashboard)
// ==========================================
const emptyKpis = {
    totalPayable: 0,
    dueThisMonth: 0,
    overdue: 0,
    paymentHealth: { onTime: 0, dueSoon: 0, overdue: 0 },
};

// ==========================================
// SHARED STYLE HELPERS (matches CFODashboard.js)
// ==========================================
const card = {
    background: "#fff",
    borderRadius: "14px",
    border: "1px solid #e2e8f0",
    boxShadow: "0 4px 6px -1px rgba(0,0,0,.05),0 2px 4px -1px rgba(0,0,0,.03)",
    padding: "20px 22px",
};

const cardHeader = {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    marginBottom: "14px",
};

const iconBadge = (bg, color) => ({
    width: "34px",
    height: "34px",
    borderRadius: "10px",
    background: bg,
    color: color,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
});

const viewAllLink = {
    fontSize: "13px",
    fontWeight: 700,
    color: "#3b82f6",
    cursor: "pointer",
};

const table = { width: "100%", borderCollapse: "collapse", fontSize: "13px" };
const th = { textAlign: "left", padding: "8px 6px", color: "#94a3b8", fontWeight: 600, fontSize: "12px", borderBottom: "1px solid #f1f5f9" };
const td = { padding: "10px 6px", color: "#334155", borderBottom: "1px solid #f1f5f9" };

const statusBadge = (status) => {
    const map = {
        Overdue: { bg: "#fee2e2", color: "#dc2626" },
        "Due Soon": { bg: "#fef3c7", color: "#d97706" },
        Current: { bg: "#dcfce7", color: "#16a34a" },
    };
    const c = map[status] || { bg: "#e2e8f0", color: "#334155" };
    return {
        background: c.bg,
        color: c.color,
        fontSize: "11.5px",
        fontWeight: 700,
        padding: "4px 10px",
        borderRadius: "999px",
        display: "inline-block",
    };
};

const verificationBadge = (v) => {
    const map = {
        Verified: { bg: "#dcfce7", color: "#16a34a" },
        "Not Verified": { bg: "#fee2e2", color: "#dc2626" },
        "Partially Verified": { bg: "#fef3c7", color: "#d97706" },
    };
    const c = map[v] || { bg: "#e2e8f0", color: "#334155" };
    return { background: c.bg, color: c.color, fontSize: "11.5px", fontWeight: 700, padding: "4px 10px", borderRadius: "999px", display: "inline-block", whiteSpace: "nowrap" };
};

// ==========================================
// SMALL COMPONENTS
// ==========================================
function Modal({ title, onClose, children }) {
    return (
        <div
            onClick={onClose}
            style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,.45)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: "24px" }}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                style={{ ...card, width: "min(1100px, 100%)", maxHeight: "85vh", display: "flex", flexDirection: "column" }}
            >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "14px" }}>
                    <span style={{ fontSize: "16px", fontWeight: 700, color: "#0f172a" }}>{title}</span>
                    <button onClick={onClose} style={{ border: "none", background: "transparent", fontSize: "22px", cursor: "pointer", color: "#64748b" }}>×</button>
                </div>
                <div style={{ overflow: "auto" }}>{children}</div>
            </div>
        </div>
    );
}

function DonutChart({ data, centerValue, centerLabel }) {
    return (
        <div style={{ position: "relative", width: "170px", height: "170px", flexShrink: 0 }}>
            <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                    <Pie data={data} dataKey="value" nameKey="name" innerRadius={52} outerRadius={80} paddingAngle={2} stroke="none">
                        {data.map((entry, i) => (
                            <Cell key={i} fill={entry.color} />
                        ))}
                    </Pie>
                    <Tooltip formatter={(val, name, props) => `₹${val} ${props?.payload?.unit || ""}`} />
                </PieChart>
            </ResponsiveContainer>
            <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", textAlign: "center" }}>
                <div style={{ fontSize: "17px", fontWeight: 800, color: "#0f172a" }}>{centerValue}</div>
                <div style={{ fontSize: "10.5px", color: "#64748b" }}>{centerLabel}</div>
            </div>
        </div>
    );
}

function PaymentHealthCard({ paymentHealth }) {
    const { onTime, dueSoon, overdue } = paymentHealth;
    return (
        <div style={card}>
            <div style={{ fontSize: "14px", fontWeight: 700, color: "#334155", marginBottom: "14px" }}>
                Payment Health
            </div>
            <div style={{ display: "flex", height: "10px", borderRadius: "6px", overflow: "hidden", marginBottom: "14px" }}>
                <div style={{ width: `${onTime}%`, background: "#22c55e" }} />
                <div style={{ width: `${dueSoon}%`, background: "#f59e0b" }} />
                <div style={{ width: `${overdue}%`, background: "#ef4444" }} />
            </div>
            <div style={{ display: "flex", gap: "16px", flexWrap: "wrap", fontSize: "12.5px", color: "#334155", fontWeight: 600 }}>
                <span><span style={{ color: "#22c55e" }}>●</span> {onTime}% On Time</span>
                <span><span style={{ color: "#f59e0b" }}>●</span> {dueSoon}% Due Soon</span>
                <span><span style={{ color: "#ef4444" }}>●</span> {overdue}% Overdue</span>
            </div>
        </div>
    );
}

function getCurrentMonthRange() {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return { start, end };
}

function formatRangeLabel(start, end) {
    if (!start || !end) return "Select date range";
    const startStr = start.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    const endStr = end.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    return `${startStr} – ${endStr}`;
}

function PayablesDashboard() {
    const [refreshing, setRefreshing] = useState(false);
    const { start: defaultStart, end: defaultEnd } = getCurrentMonthRange();
    const [startDate, setStartDate] = useState(defaultStart);
    const [endDate, setEndDate] = useState(defaultEnd);
    const [showPicker, setShowPicker] = useState(false);
    const pickerRef = useRef(null);

    const [kpis, setKpis] = useState(emptyKpis);
    const [payablesAging, setPayablesAging] = useState([]);
    const [payablesDueTrend, setPayablesDueTrend] = useState([]);
    const [payablesDueTrendUnit, setPayablesDueTrendUnit] = useState("");
    const [topSuppliers, setTopSuppliers] = useState([]);
    const [recentPayables, setRecentPayables] = useState([]);
    const [locations, setLocations] = useState([]);
    const [selectedLocation, setSelectedLocation] = useState("all");
    const [showLocation, setShowLocation] = useState(false);
    const locationRef = useRef(null);
    const [modal, setModal] = useState(null); // null | "suppliers" | "payables"
    const [modalData, setModalData] = useState([]);
    const [modalLoading, setModalLoading] = useState(false);
    const [modalError, setModalError] = useState(null);
    const [verificationFilter, setVerificationFilter] = useState("all");
    const [dashboardLoading, setDashboardLoading] = useState(true);
    const [dashboardError, setDashboardError] = useState(null);

    const fetchDashboard = async (location = selectedLocation) => {
        setDashboardLoading(true);
        setDashboardError(null);
        try {
            const { data } = await axios.get(`${PURCHASE_API}/payables-dashboard/dashboard`, {
                params: { location },
            });
            if (!data.success) throw new Error(data.message || "Failed to load Payables Dashboard");
            setLocations(data.locations || []);
            setKpis(data.kpis);
            setPayablesAging(data.payablesAging);
            setPayablesDueTrend(data.payablesDueTrend);
            setPayablesDueTrendUnit(data.payablesDueTrendUnit || "");
            setTopSuppliers(data.topSuppliers);
            setRecentPayables(data.recentPayables);
        } catch (err) {
            console.error("Payables Dashboard fetch error:", err);
            setDashboardError(err.response?.data?.message || err.message || "Failed to load data");
        } finally {
            setDashboardLoading(false);
        }
    };

    useEffect(() => {
        fetchDashboard();
    }, []);

    const openModal = async (type, verification = "all") => {
        setModal(type);
        setModalData([]);
        setModalError(null);
        setModalLoading(true);
        try {
            const endpoint = type === "suppliers" ? "suppliers" : "payables";
            const { data } = await axios.get(`${PURCHASE_API}/payables-dashboard/${endpoint}`, {
                params: { location: selectedLocation, verification },
            });
            if (!data.success) throw new Error(data.message || "Failed to load list");
            setModalData(type === "suppliers" ? data.suppliers : data.payables);
        } catch (err) {
            setModalError(err.response?.data?.message || err.message || "Failed to load list");
        } finally {
            setModalLoading(false);
        }
    };

    const handleLocationChange = (loc) => {
        setSelectedLocation(loc);
        setShowLocation(false);
        fetchDashboard(loc);
    };

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (pickerRef.current && !pickerRef.current.contains(e.target)) {
                setShowPicker(false);
            }
            if (locationRef.current && !locationRef.current.contains(e.target)) {
                setShowLocation(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleDateChange = (dates) => {
        const [start, end] = dates;
        setStartDate(start);
        setEndDate(end);
        if (start && end) setShowPicker(false);
    };

    const handleRefresh = () => {
        setRefreshing(true);
        fetchDashboard(selectedLocation);
        setTimeout(() => setRefreshing(false), 600);
    };

    const agingUnit = payablesAging[0]?.unit || "";
    const totalAging = payablesAging.reduce((s, d) => s + d.value, 0).toFixed(1);

    return (
    <Layout defaultSidebarOpen={false}>
            <div
                style={{
                    padding: "24px 32px",
                    backgroundColor: "#f8fafc",
                    minHeight: "calc(100vh - 60px)",
                }}
            >
                {/* ================= HEADER ================= */}
                <div
                    style={{
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                        flexWrap: "wrap",
                        gap: "16px",
                        marginBottom: "28px",
                    }}
                >
                    <div>
                        <h2 style={{ margin: "0 0 6px 0", color: "#0f172a", fontWeight: 700, fontSize: "26px" }}>
                           Accounts Payables Dashboard
                        </h2>
                        <p style={{ color: "#64748b", margin: 0, fontSize: "14px" }}>
                            Track supplier payments and upcoming liabilities
                        </p>
                    </div>

                    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                        <div style={{ position: "relative" }} ref={pickerRef}>
                            <div
                                onClick={() => setShowPicker((prev) => !prev)}
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                    background: "#fff",
                                    border: "1px solid #e2e8f0",
                                    borderRadius: "10px",
                                    padding: "9px 14px",
                                    fontSize: "13px",
                                    color: "#334155",
                                    fontWeight: 500,
                                    cursor: "pointer",
                                }}
                            >
                                <FaCalendarAlt size={13} color="#64748b" />
                                {formatRangeLabel(startDate, endDate)}
                            </div>

                            {showPicker && (
                                <div
                                    style={{
                                        position: "absolute",
                                        top: "calc(100% + 8px)",
                                        right: 0,
                                        zIndex: 100,
                                        background: "#fff",
                                        borderRadius: "10px",
                                        boxShadow: "0 10px 25px rgba(0,0,0,0.15)",
                                        border: "1px solid #e2e8f0",
                                        padding: "10px",
                                    }}
                                >
                                    <DatePicker
                                        selectsRange
                                        startDate={startDate}
                                        endDate={endDate}
                                        onChange={handleDateChange}
                                        inline
                                        monthsShown={2}
                                    />
                                    <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "8px" }}>
                                        <button
                                            onClick={() => {
                                                const { start, end } = getCurrentMonthRange();
                                                setStartDate(start);
                                                setEndDate(end);
                                                setShowPicker(false);
                                            }}
                                            style={{
                                                background: "transparent",
                                                border: "1px solid #e2e8f0",
                                                borderRadius: "8px",
                                                padding: "6px 12px",
                                                fontSize: "12px",
                                                fontWeight: 600,
                                                color: "#334155",
                                                cursor: "pointer",
                                            }}
                                        >
                                            This Month
                                        </button>
                                        <button
                                            onClick={() => setShowPicker(false)}
                                            style={{
                                                background: "#2563eb",
                                                border: "none",
                                                borderRadius: "8px",
                                                padding: "6px 14px",
                                                fontSize: "12px",
                                                fontWeight: 600,
                                                color: "#fff",
                                                cursor: "pointer",
                                            }}
                                        >
                                            Apply
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div style={{ position: "relative" }} ref={locationRef}>
                            <div
                                onClick={() => setShowLocation((p) => !p)}
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                    background: "#fff",
                                    border: "1px solid #e2e8f0",
                                    borderRadius: "10px",
                                    padding: "9px 14px",
                                    fontSize: "13px",
                                    color: "#334155",
                                    fontWeight: 600,
                                    cursor: "pointer",
                                }}
                            >
                                {selectedLocation === "all" ? "All Locations" : selectedLocation} <FaChevronDown size={10} />
                            </div>
                            {showLocation && (
                                <div
                                    style={{
                                        position: "absolute",
                                        top: "calc(100% + 6px)",
                                        right: 0,
                                        minWidth: "100%",
                                        maxHeight: "260px",
                                        overflowY: "auto",
                                        zIndex: 100,
                                        background: "#fff",
                                        border: "1px solid #e2e8f0",
                                        borderRadius: "10px",
                                        boxShadow: "0 10px 25px rgba(0,0,0,0.15)",
                                    }}
                                >
                                    {["all", ...locations.filter((l) => l !== "Unassigned")].map((l) => (
                                        <div
                                            key={l}
                                            onClick={() => handleLocationChange(l)}
                                            style={{
                                                padding: "9px 14px",
                                                fontSize: "13px",
                                                cursor: "pointer",
                                                whiteSpace: "nowrap",
                                                fontWeight: l === selectedLocation ? 700 : 500,
                                                background: l === selectedLocation ? "#eff6ff" : "#fff",
                                                color: "#334155",
                                            }}
                                        >
                                            {l === "all" ? "All Locations" : l}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <button
                            onClick={handleRefresh}
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                background: "#2563eb",
                                border: "none",
                                borderRadius: "10px",
                                padding: "10px 16px",
                                fontSize: "13px",
                                fontWeight: 600,
                                color: "#fff",
                                cursor: "pointer",
                            }}
                        >
                            <FaSyncAlt size={13} style={{ animation: refreshing ? "spin 0.8s linear infinite" : "none" }} />
                            Refresh
                        </button>

                        <div
                            style={{
                                width: "38px",
                                height: "38px",
                                borderRadius: "10px",
                                background: "#fff",
                                border: "1px solid #e2e8f0",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "#334155",
                                cursor: "pointer",
                            }}
                        >
                            <FaDownload size={14} />
                        </div>
                    </div>
                </div>

                {/* ================= KPI CARDS ================= */}
                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))",
                        gap: "20px",
                        marginBottom: "24px",
                    }}
                >
                    <div style={card}>
                        <div style={cardHeader}>
                            <div style={iconBadge("#eff6ff", "#2563eb")}>
                                <FaWallet size={16} />
                            </div>
                            <span style={{ fontSize: "14px", fontWeight: 700, color: "#334155" }}>Total Payable</span>
                        </div>
                        <div style={{ fontSize: "26px", fontWeight: 800, color: "#0f172a" }}>
                            {dashboardLoading
                                ? "Loading…"
                                : dashboardError
                                    ? "—"
                                    : `₹ ${kpis.totalPayable} ${kpis.totalPayableUnit || ""}`}
                        </div>
                    </div>

                    <div style={card}>
                        <div style={cardHeader}>
                            <div style={iconBadge("#f0fdf4", "#16a34a")}>
                                <FaCalendarAlt size={16} />
                            </div>
                            <span style={{ fontSize: "14px", fontWeight: 700, color: "#334155" }}>Due This Month</span>
                        </div>
                        <div style={{ fontSize: "26px", fontWeight: 800, color: "#0f172a" }}>
                            {dashboardLoading ? "Loading…" : dashboardError ? "—" : `₹ ${kpis.dueThisMonth} ${kpis.dueThisMonthUnit || ""}`}
                        </div>
                    </div>

                    <div style={card}>
                        <div style={cardHeader}>
                            <div style={iconBadge("#fff7ed", "#f97316")}>
                                <FaChartLine size={16} />
                            </div>
                            <span style={{ fontSize: "14px", fontWeight: 700, color: "#334155" }}>Overdue</span>
                        </div>
                        <div style={{ fontSize: "26px", fontWeight: 800, color: "#0f172a" }}>
                            {dashboardLoading ? "Loading…" : dashboardError ? "—" : `₹ ${kpis.overdue} ${kpis.overdueUnit || ""}`}
                        </div>
                    </div>

                    <PaymentHealthCard paymentHealth={kpis.paymentHealth} />
                </div>

                {dashboardError && (
                    <div style={{ color: "#dc2626", fontSize: "13px", marginBottom: "16px" }}>
                        {dashboardError}
                    </div>
                )}

                {/* ================= CHARTS ROW ================= */}
                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                        gap: "20px",
                        marginBottom: "20px",
                    }}
                >
                    {/* Payables Aging */}
                    <div style={card}>
                        <div style={{ fontSize: "14px", fontWeight: 700, color: "#334155", marginBottom: "14px" }}>
                            Payables Aging
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: "18px", flexWrap: "wrap" }}>
                            <DonutChart data={payablesAging} centerValue={`₹${totalAging} ${agingUnit}`} centerLabel="Total Payable" />
                            <div style={{ flex: 1, minWidth: "150px", display: "flex", flexDirection: "column", gap: "10px" }}>
                                {payablesAging.map((d) => (
                                    <div key={d.name} style={{ fontSize: "12.5px" }}>
                                        <div style={{ display: "flex", alignItems: "center", gap: "7px" }}>
                                            <span style={{ width: 9, height: 9, borderRadius: "50%", background: d.color, display: "inline-block" }} />
                                            <span style={{ color: "#334155", fontWeight: 600 }}>{d.name}</span>
                                        </div>
                                        <div style={{ marginLeft: "16px", color: "#0f172a", fontWeight: 700 }}>
                                            ₹{d.value} {d.unit} <span style={{ color: "#94a3b8", fontWeight: 500 }}>({d.pct}%)</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Payables Due Trend */}
                    <div style={card}>
                        <div style={{ fontSize: "14px", fontWeight: 700, color: "#334155", marginBottom: "14px" }}>
                            Payables Due Trend {payablesDueTrendUnit ? `(₹ ${payablesDueTrendUnit})` : "(₹)"}
                        </div>
                        <ResponsiveContainer width="100%" height={210}>
                            <BarChart data={payablesDueTrend}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                <XAxis dataKey="week" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
                                <YAxis tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
                                <Tooltip formatter={(val) => `₹${val} ${payablesDueTrendUnit}`} />
                                <Bar dataKey="value" fill="#2563eb" radius={[4, 4, 0, 0]} barSize={30} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>

                    {/* Top Suppliers */}
                    <div style={card}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "14px" }}>
                            <span style={{ fontSize: "14px", fontWeight: 700, color: "#334155" }}>Top Suppliers</span>
                            <span style={viewAllLink} onClick={() => openModal("suppliers")}>View All</span>
                        </div>
                        <table style={table}>
                            <thead>
                                <tr>
                                    <th style={th}>Supplier</th>
                                    <th style={{ ...th, textAlign: "right" }}>Payable</th>
                                    <th style={{ ...th, textAlign: "right" }}>Overdue</th>
                                </tr>
                            </thead>
                            <tbody>
                                {topSuppliers.map((s) => (
                                    <tr key={s.name}>
                                        <td style={td}>{s.name}</td>
                                        <td style={{ ...td, textAlign: "right", fontWeight: 700, color: "#0f172a" }}>₹{s.payable} {s.payableUnit}</td>
                                        <td style={{ ...td, textAlign: "right", color: "#ef4444", fontWeight: 700 }}>₹{s.overdue} {s.overdueUnit}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* ================= RECENT PAYABLES ================= */}
                <div style={card}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "14px" }}>
                        <span style={{ fontSize: "14px", fontWeight: 700, color: "#334155" }}>Recent Payables</span>
                        <span style={viewAllLink} onClick={() => { setVerificationFilter("all"); openModal("payables", "all"); }}>View All</span>
                    </div>
                    <div style={{ overflowX: "auto" }}>
                        <table style={table}>
                            <thead>
                                <tr>
                                    <th style={th}>Invoice No.</th>
                                    <th style={th}>Supplier</th>
                                    <th style={th}>Invoice Date</th>
                                    <th style={th}>Due Date</th>
                                    <th style={{ ...th, textAlign: "right" }}>Amount (₹)</th>
                                    <th style={{ ...th, textAlign: "right" }}>Outstanding (₹)</th>
                                    <th style={th}>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {recentPayables.map((r) => (
                                    <tr key={r.invoiceNo}>
                                        <td style={{ ...td, fontWeight: 700, color: "#0f172a" }}>{r.invoiceNo}</td>
                                        <td style={td}>{r.supplier}</td>
                                        <td style={td}>{r.invoiceDate}</td>
                                        <td style={td}>{r.dueDate}</td>
                                        <td style={{ ...td, textAlign: "right" }}>{r.amount}</td>
                                        <td style={{ ...td, textAlign: "right" }}>{r.outstanding}</td>
                                        <td style={td}>
                                            <span style={statusBadge(r.status)}>{r.status}</span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* ================= VIEW ALL MODAL ================= */}
                {modal && (
                    <Modal title={modal === "suppliers" ? "All Suppliers" : "All Payables"} onClose={() => setModal(null)}>
                        {modal === "payables" && (
                            <div style={{ display: "flex", gap: "8px", marginBottom: "12px" }}>
                                {[["all", "All"], ["verified", "Verified"], ["not_verified", "Not Verified"]].map(([val, label]) => (
                                    <button
                                        key={val}
                                        onClick={() => { setVerificationFilter(val); openModal("payables", val); }}
                                        style={{
                                            border: "1px solid #e2e8f0", borderRadius: "999px", padding: "5px 14px", fontSize: "12.5px", fontWeight: 600, cursor: "pointer",
                                            background: verificationFilter === val ? "#2563eb" : "#fff",
                                            color: verificationFilter === val ? "#fff" : "#334155",
                                        }}
                                    >
                                        {label}
                                    </button>
                                ))}
                            </div>
                        )}
                        {modalLoading ? (
                            <div style={{ padding: "20px", color: "#64748b" }}>Loading…</div>
                        ) : modalError ? (
                            <div style={{ padding: "20px", color: "#dc2626" }}>{modalError}</div>
                        ) : modal === "suppliers" ? (
                            <table style={table}>
                                <thead>
                                    <tr>
                                        <th style={th}>Supplier</th>
                                        <th style={{ ...th, textAlign: "right" }}>Invoices</th>
                                        <th style={{ ...th, textAlign: "right" }}>Payable</th>
                                        <th style={{ ...th, textAlign: "right" }}>Verified</th>
                                        <th style={{ ...th, textAlign: "right" }}>Not Verified</th>
                                        <th style={{ ...th, textAlign: "right" }}>Overdue</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {modalData.map((r) => (
                                        <tr key={r.name}>
                                            <td style={td}>{r.name}</td>
                                            <td style={{ ...td, textAlign: "right" }}>{r.invoices}</td>
                                            <td style={{ ...td, textAlign: "right", fontWeight: 700 }}>{r.payable.label}</td>
                                            <td style={{ ...td, textAlign: "right", color: "#16a34a" }}>{r.verified.label}</td>
                                            <td style={{ ...td, textAlign: "right", color: "#dc2626" }}>{r.notVerified.label}</td>
                                            <td style={{ ...td, textAlign: "right", color: "#ef4444", fontWeight: 700 }}>{r.overdue.label}</td>
                                        </tr>
                                    ))}
                                    {modalData.length === 0 && <tr><td style={td} colSpan={6}>No data</td></tr>}
                                </tbody>
                            </table>
                        ) : (
                            <table style={table}>
                                <thead>
                                    <tr>
                                        <th style={th}>Invoice No.</th>
                                        <th style={th}>Supplier</th>
                                        <th style={th}>Location</th>
                                        <th style={th}>Invoice Date</th>
                                        <th style={th}>Due Date</th>
                                        <th style={{ ...th, textAlign: "right" }}>Days Overdue</th>
                                        <th style={{ ...th, textAlign: "right" }}>Amount (₹)</th>
                                        <th style={{ ...th, textAlign: "right" }}>Outstanding (₹)</th>
                                        <th style={th}>Verification</th>
                                        <th style={th}>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {modalData.map((r, i) => (
                                        <tr key={`${r.invoiceNo}-${i}`}>
                                            <td style={{ ...td, fontWeight: 700, color: "#0f172a" }}>{r.invoiceNo}</td>
                                            <td style={td}>{r.supplier}</td>
                                            <td style={td}>{r.location}</td>
                                            <td style={td}>{r.invoiceDate}</td>
                                            <td style={td}>{r.dueDate}</td>
                                            <td style={{ ...td, textAlign: "right" }}>{r.daysOverdue || "-"}</td>
                                            <td style={{ ...td, textAlign: "right" }}>{r.amount}</td>
                                            <td style={{ ...td, textAlign: "right" }}>{r.outstanding}</td>
                                            <td style={td}><span style={verificationBadge(r.verification)}>{r.verification}</span></td>
                                            <td style={td}><span style={statusBadge(r.status)}>{r.status}</span></td>
                                        </tr>
                                    ))}
                                    {modalData.length === 0 && <tr><td style={td} colSpan={10}>No data</td></tr>}
                                </tbody>
                            </table>
                        )}
                    </Modal>
                )}

                <div style={{ textAlign: "center", fontSize: "12px", color: "#94a3b8", marginTop: "24px" }}>
                    Amounts shown in Thousand (K) / Lakh (L) / Crore (Cr) as applicable &nbsp;|&nbsp; Data is real-time and synchronized across all locations
                </div>

                <style>{`
                    @keyframes spin {
                        from { transform: rotate(0deg); }
                        to { transform: rotate(360deg); }
                    }
                `}</style>
            </div>
        </Layout>
    );
}

export default PayablesDashboard;